import "./style.scss";

import CloseIcon from "@mui/icons-material/Close";
import InsertInvitationOutlinedIcon from "@mui/icons-material/InsertInvitationOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { TabContext, TabPanel } from "@mui/lab";
import { Avatar, IconButton, InputAdornment, Tab, TableCell, TableRow } from "@mui/material";
import IsOnline from "assets/img/svg/isOnline";
import PickerCompany from "components/BPPickerCompany";
import ShowStatements from "components/shared/BPShowStatements";
import EmptyState from "components/shared/EmptyState";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import StatementStatusChip from "components/shared/StatementStatusChip";
import { StyledTabs } from "components/shared/StyledTabs";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import YearDatePicker from "components/shared/YearDatePicker";
import { formatLocaleDateString, formatNumber, incrementDateYears, isNewDate } from "helpers/utils";
import { useIsMobile } from "hooks/useIsMobile";
import usePagination from "hooks/usePagination";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { getCompagnies } from "store/beneficiaryStore";
import type { Currencies } from "store/settingsStore";
import {
    fetchBPStatementsByArticles,
    fetchBPStatementsByBeneficiars,
    fetchBPStatementsByTitle,
} from "store/statementsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { IBeneficiariesCompanies, ILastStatement } from "store/types";

const BPStatement = () => {
    const [filterValue, setFilterValue] = useState("");
    const [date, setDate] = useState(null);
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { isMobile } = useIsMobile();
    const [isOpen, setIsOpen] = useState(false);
    const [currentPeriodId, setCurrentPeriodId] = useState<number | null>(null);
    const [currentBeneficiarId, setCurrentBeneficiarId] = useState<number | null>(null);
    const [currentAdvancePaymentId, setCurrentAdvancePaymentId] = useState<number | null>(null);
    const [currentArticleId, setCurrentArticleId] = useState<number | null>(null);
    const [currentTerm, setCurrentTerm] = useState<ILastStatement | null>(null);
    const [currentStatementIndex, setCurrentStatementIndex] = useState<number | null>(null);
    const [currentTab, setCurrentTab] = useState("0");
    const [chosenCompanies, setChosenCompanies] = useState<IBeneficiariesCompanies[]>([]);
    const { bpStatementsByTitle, bpStatementsByBeneficiars, bpStatementsByArticles } = useAppSelector(
        (state) => state.statements,
    );
    const { beneficiarCompagnies } = useAppSelector((state) => state.beneficiary);
    const [orderByLocal, setOrderByLocal] = useState();

    const handleChangeFilterValue = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setFilterValue(e.target.value);
    const handleChangeDate = (newValue: any) => setDate(newValue);

    const mainBeneficiaryPortalType = beneficiarCompagnies[0]?.publisherType;

    const sortByPeriod = (statements: ILastStatement[], orderBy: string, orderAsc: string): ILastStatement[] => {
        let sortedByPeriods = statements.reduce((acc: any, obj) => {
            const periodYearEnd = new Date(obj.periodEnd).getUTCFullYear();
            if (!acc[periodYearEnd] && !isNaN(periodYearEnd)) {
                acc[periodYearEnd] = [];
            }
            if (isNaN(periodYearEnd) && !acc[9999]) acc[9999] = [];
            if (isNaN(periodYearEnd)) {
                acc[9999].push(obj);
                return acc;
            }
            acc[periodYearEnd].push(obj);
            return acc;
        }, {});
        sortedByPeriods = Object.values(sortedByPeriods)
            .map((item: any) => item)
            .reverse();
        if (orderBy == "period" && orderAsc == "desc") sortedByPeriods.reverse();
        let sortedStatement: any = [];
        sortedByPeriods.map((value: any) => {
            const newPeriodicStatement = value.sort((a: any, b: any) => {
                if (typeof a[orderBy] === "number") {
                    if (+a[orderBy] - +b[orderBy] > 0) {
                        return orderAsc === "asc" ? 1 : -1;
                    } else {
                        return orderAsc === "asc" ? -1 : 1;
                    }
                }
                if (typeof a[orderBy] === "string") {
                    if (a[orderBy].localeCompare(b[orderBy]) > 0) {
                        return orderAsc === "asc" ? 1 : -1;
                    } else {
                        return orderAsc === "asc" ? -1 : 1;
                    }
                }
                return 0;
            });
            sortedStatement = [...sortedStatement, ...newPeriodicStatement];
        });
        return sortedStatement;
    };

    const filteredStatementTitleArray = useMemo(() => {
        const bpStatementsByTitleWithAdvanceYear = bpStatementsByTitle.map((item) =>
            item.periodEnd ? item : { ...item, periodEnd: item.paymentDate! },
        );
        bpStatementsByTitleWithAdvanceYear.push(...bpStatementsByArticles);
        return bpStatementsByTitleWithAdvanceYear.filter(
            (item) =>
                (item.name?.toLowerCase().includes(filterValue.toLowerCase()) ||
                    (item as any).titleName?.toLowerCase().includes(filterValue.toLowerCase())) &&
                (date ? new Date(item.periodEnd).getUTCFullYear() == new Date(date).getUTCFullYear() : true) &&
                (chosenCompanies.length > 0 ? chosenCompanies.some((comp) => comp.id == item.companyId) : false),
        );
    }, [date, filterValue, bpStatementsByTitle, chosenCompanies, orderByLocal]);

    const filteredStatementBeneficiarArray = useMemo(() => {
        return bpStatementsByBeneficiars.filter(
            (item) =>
                (item.name?.toLowerCase().includes(filterValue.toLowerCase()) ||
                    (item as any).titleName?.toLowerCase().includes(filterValue.toLowerCase())) &&
                (date ? new Date(item.periodEnd).getUTCFullYear() == new Date(date).getUTCFullYear() : true) &&
                (chosenCompanies.length > 0 ? chosenCompanies.some((comp) => comp.id == item.companyId) : false),
        );
    }, [date, filterValue, bpStatementsByBeneficiars, chosenCompanies]);

    const maxDateFromPeriod = useMemo(
        () =>
            bpStatementsByTitle.reduce((acc, curr) => {
                return new Date(curr.periodEnd) > acc ? new Date(curr.periodEnd) : acc;
            }, new Date()),
        [bpStatementsByTitle],
    );

    const minDateFromPeriod = useMemo(
        () =>
            bpStatementsByTitle.reduce((acc, curr) => {
                return new Date(curr.periodEnd).getUTCFullYear() < acc
                    ? new Date(curr.periodEnd).getUTCFullYear()
                    : acc;
            }, 9999),
        [bpStatementsByTitle],
    );
    // }, new Date().getFullYear()), [bpStatementsByTitle]) // use this if the minimum date from period are superior to current year and you still want to use this year

    const [dividedByPageSTP, orderBySTP] = usePagination(filteredStatementTitleArray, sortByPeriod);

    const toggleDrawer =
        (anchor: any, open: any): any =>
        (event: any) => {
            if (
                event &&
                event.type === "keydown" &&
                ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")
            ) {
                return;
            }
            setIsOpen(open);
        };
    const handleShowStatements = (item: ILastStatement, i: number) => {
        if (!item) return;
        setCurrentPeriodId(item.periodId);
        if (item.type == "article") {
            setCurrentArticleId(item.id);
            setCurrentBeneficiarId(null);
            setCurrentAdvancePaymentId(null);
        } else {
            setCurrentArticleId(null);
            setCurrentBeneficiarId(item.sliceId ? null : item.id);
            setCurrentAdvancePaymentId(item.sliceId ? item.id : null);
        }
        setCurrentTerm(item);
        setCurrentStatementIndex(i);
        setIsOpen(true);
    };

    const renderInputDatePicker = (params: any) => {
        return (
            <StyledTextField
                placeholder="yyyy"
                fullWidth
                {...params}
                InputProps={{
                    endAdornment: (
                        <>
                            <InputAdornment position="end">
                                {date && (
                                    <IconButton onClick={() => setDate(null)} edge="end">
                                        <CloseIcon />
                                    </IconButton>
                                )}
                            </InputAdornment>
                            {isMobile ? (
                                <InsertInvitationOutlinedIcon
                                    style={{ color: "#A8A8A8", height: "20px", width: "20px" }}
                                />
                            ) : (
                                params.InputProps?.endAdornment
                            )}
                        </>
                    ),
                }}
            />
        );
    };

    useEffect(() => {
        dispatch(fetchBPStatementsByTitle());
        dispatch(fetchBPStatementsByArticles());
        dispatch(fetchBPStatementsByBeneficiars());
        dispatch(getCompagnies());
    }, []);

    useEffect(() => {
        setChosenCompanies(beneficiarCompagnies);
    }, [beneficiarCompagnies]);

    useEffect(() => {
        if (location.state == null) return;
        if (!dividedByPageSTP) return;
        const state: { termId: number; periodId: number; type: string } = location.state as {
            termId: number;
            periodId: number;
            type: string;
        };
        let selectedTerm;
        let selectedTermIndex;
        if (state.type == "consolidated") {
            setCurrentTab("1");
            selectedTerm = bpStatementsByBeneficiars.find(
                (item) => item.id == state.termId && item.periodId == state.periodId,
            );
            selectedTermIndex = bpStatementsByBeneficiars.findIndex(
                (item) => item.id == state.termId && item.periodId == state.periodId,
            );
        } else {
            selectedTerm = bpStatementsByTitle.find(
                (item) => item.termId == state.termId && item.periodId == state.periodId,
            );
            selectedTermIndex = bpStatementsByTitle.findIndex(
                (item) => item.termId == state.termId && item.periodId == state.periodId,
            );
        }
        if (!selectedTerm) return;
        handleShowStatements(selectedTerm, selectedTermIndex);
        location.state = null;
    }, [location, dividedByPageSTP]);

    useEffect(() => {
        setOrderByLocal(orderBySTP as any);
    }, [orderBySTP]);

    const statementsByTitlesColumns: LastElementTableColumns[] = [
        {
            id: "name",
            label: t(`BPStatement.StatementTitle_${mainBeneficiaryPortalType}`),
            align: "left",
            avatar: false,
            subtitle: "titleName",
            sort: true,
        },
        {
            id: "type",
            label: t("BPStatement.Type"),
            align: "left",
            format: (value: any) => t(`BPStatement.${value}`),
        },
        {
            id: "grossDuty",
            label: t("BPStatement.Gross"),
            align: "left",
            sort: true,
            format: (value: any) =>
                formatNumber(value / 100, {
                    isCurrency: true,
                    always2Digit: true,
                    preferredCurrency: chosenCompanies[0]?.currency as Currencies,
                }),
        },
        {
            id: "periodStart",
            label: t("BPStatement.Period"),
            align: "left",
            sort: true,
            format: (value: any, id, row) => {
                if (row.type === "title") {
                    return `${formatLocaleDateString({
                        date: value,
                        options: { numeric: false, timeZone: "UTC" },
                    })} → ${formatLocaleDateString({ date: row.periodEnd })}`;
                } else if (row.type === "article" || row.type === "advance") return "-";
            },
        },
        {
            id: "isPeriodActive",
            label: t("BPStatement.StatementStatus"),
            align: "left",
            format: (value: any, id, row) => {
                if (row.type === "title") return <StatementStatusChip status={value} />;
            },
        },
        {
            id: "",
            label: t("BPStatement.Action"),
            align: "right",
            format: (value: any, id, row) => (
                <span
                    className="bpstatement-details"
                    onClick={(e) => handleShowStatements(row, filteredStatementTitleArray.indexOf(row))}
                >
                    {t("Articles.See")}
                </span>
            ),
        },
    ];

    const statementsByPeriodsColumns: LastElementTableColumns[] = [
        { id: "name", label: t("BPDashboard.Title"), align: "left", sort: true },
        {
            id: "grossDuty",
            label: t("BPStatement.Gross"),
            align: "left",
            sort: true,
            format: (value: any) =>
                formatNumber(value / 100, {
                    isCurrency: true,
                    always2Digit: true,
                    preferredCurrency: chosenCompanies[0]?.currency as Currencies,
                }),
        },
        {
            id: "periodStart",
            label: t("BPStatement.Period"),
            align: "left",
            sort: true,
            format: (value: any, id, row) =>
                `${formatLocaleDateString({ date: value })} → ${formatLocaleDateString({ date: row.periodEnd })}`,
        },
        {
            id: "isPeriodActive",
            label: t("BPStatement.StatementStatus"),
            align: "left",
            format: (value: any, id, row) => <StatementStatusChip status={value} />,
        },
        {
            id: "",
            label: t("BPStatement.Action"),
            align: "right",
            format: (value: any, id, row) => (
                <span
                    className="bpstatement-details"
                    onClick={(e) => handleShowStatements(row, filteredStatementTitleArray.indexOf(row))}
                >
                    {t("Articles.See")}
                </span>
            ),
        },
    ];

    const separatorContent = (statement: any) => {
        return (
            <>
                {!isMobile && (
                    <TableRow>
                        <TableCell style={{ padding: 0, border: "none" }} colSpan={6}>
                            <div className="bpstatement-newperiod-separator">
                                <span>{new Date(statement.periodEnd).getUTCFullYear()}</span>
                            </div>
                        </TableCell>
                    </TableRow>
                )}
                {isMobile && (
                    <div className="bpstatement-newperiod-separator">
                        <span>{new Date(statement.periodEnd).getUTCFullYear()}</span>
                    </div>
                )}
            </>
        );
    };

    const renderMobileStatementRow = (statement: any) => {
        return (
            <div className="list-mobile-element">
                <div className="element-left">
                    <div className="element-title-wrapper">
                        <Avatar src={statement.avatarUrl} variant="rounded" />
                        <span className="element-title">{statement.name}</span>
                    </div>
                    <span className="element-subtitle">{`${formatLocaleDateString({
                        date: statement.periodStart,
                    })} → ${formatLocaleDateString({ date: statement.periodEnd })}`}</span>
                </div>
            </div>
        );
    };

    return (
        <div className="payment-wrapper bp">
            <ShowStatements
                isOpen={isOpen}
                setOpen={setIsOpen}
                toggleDrawer={toggleDrawer}
                periodId={currentPeriodId}
                beneficiarId={currentBeneficiarId}
                advancePaymentId={currentAdvancePaymentId}
                articleId={currentArticleId}
                term={currentTerm}
                title={currentTerm?.name}
                allStatements={+currentTab == 0 ? filteredStatementTitleArray : filteredStatementBeneficiarArray}
                statementIndex={currentStatementIndex}
                setNewStatement={(e: ILastStatement, i: number) => handleShowStatements(e, i)}
                beneficiarCompanies={beneficiarCompagnies}
            />
            <header className="payment-header">
                <h1 className="payment-heading">{t("BPStatement.Heading")}</h1>
                {/* <div className="export-button-wrapper">
                    <StyledButton sx={buttonStyles}>
                        <Downloadnew />
                        {t("Payment.Export")}
                    </StyledButton>
                </div> */}
            </header>
            <main className="payment-main">
                <div className="filter-wrapper">
                    <div className="filter-input">
                        <StyledTextField
                            value={filterValue}
                            onChange={handleChangeFilterValue}
                            placeholder={t(`BPStatement.SearchPlaceholder_${mainBeneficiaryPortalType}`)}
                            fullWidth
                            InputProps={{
                                endAdornment: <SearchIcon sx={{ color: "#A8A8A8" }} />,
                            }}
                        />
                    </div>
                    <div className="filter-picker">
                        {beneficiarCompagnies.length > 1 && (
                            <PickerCompany
                                data={beneficiarCompagnies}
                                selectedData={chosenCompanies}
                                setSelectedData={setChosenCompanies}
                            />
                        )}
                        <YearDatePicker
                            label={t("Payment.Year")}
                            date={date}
                            minDate={new Date().setFullYear((minDateFromPeriod ?? 0) - 1)}
                            maxDate={incrementDateYears({ yearDistance: 1, referenceDate: maxDateFromPeriod })}
                            onChange={handleChangeDate}
                            renderInput={(params: any) => renderInputDatePicker(params)}
                        />
                    </div>
                </div>
                <div className="statements-table-body">
                    <TabContext value={currentTab.toString()}>
                        <StyledTabs
                            value={+currentTab}
                            onChange={(_, tab) => setCurrentTab(tab)}
                            className="bpstatement-table-tabs"
                        >
                            <Tab label={t(`BPStatement.StatementByTitle_${mainBeneficiaryPortalType}`)} />
                            {!["press", "audiovisual"].includes(mainBeneficiaryPortalType ?? "") && (
                                <Tab label={t("BPStatement.StatementByBeneficiar")} />
                            )}
                        </StyledTabs>
                        <TabPanel sx={{ padding: 0 }} value="0">
                            <div className="table-wrapper">
                                {filteredStatementTitleArray.length == 0 ? (
                                    <EmptyState type="generic" sx={{ margin: "auto" }} />
                                ) : (
                                    <div className="bp-statements-table">
                                        <LastElementTable
                                            isMobile={isMobile}
                                            renderMobileRow={(element) => renderMobileStatementRow(element)}
                                            columns={statementsByTitlesColumns}
                                            data={filteredStatementTitleArray}
                                            onRowClick={(id: any, id2: any, type: any, row: any) =>
                                                handleShowStatements(row, filteredStatementTitleArray.indexOf(row))
                                            }
                                            isNewDate={(row: any, index: number, data: any) =>
                                                isNewDate(row, index, data, "periodEnd")
                                            }
                                            separatorContent={(statement: any) => separatorContent(statement)}
                                            dataType="statements"
                                            stickyFooter
                                            max={-1}
                                        />
                                    </div>
                                )}
                            </div>
                        </TabPanel>
                        {!["press", "audiovisual"].includes(mainBeneficiaryPortalType ?? "") && (
                            <TabPanel sx={{ padding: 0 }} value="1">
                                <div className="table-wrapper">
                                    {filteredStatementBeneficiarArray.length == 0 ? (
                                        <EmptyState type="generic" sx={{ margin: "auto" }} />
                                    ) : (
                                        <div className="bp-statements-table">
                                            <LastElementTable
                                                columns={statementsByPeriodsColumns}
                                                data={filteredStatementBeneficiarArray}
                                                isMobile={isMobile}
                                                renderMobileRow={(element) => renderMobileStatementRow(element)}
                                                onRowClick={(id: any, id2: any, type: any, row: any) =>
                                                    handleShowStatements(row, filteredStatementTitleArray.indexOf(row))
                                                }
                                                isNewDate={(row: any, index: number, data: any) =>
                                                    isNewDate(row, index, data, "periodEnd")
                                                }
                                                separatorContent={(statement: any) => separatorContent(statement)}
                                                stickyFooter
                                                dataType="statements"
                                                max={-1}
                                            />
                                        </div>
                                    )}
                                </div>
                            </TabPanel>
                        )}
                    </TabContext>
                </div>
            </main>
        </div>
    );
};

export default BPStatement;
